import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {supabase} from '../../api/supabase';
import {UserProfile} from './matchSlice';

// Define types
export interface RoomMessage {
  id: string;
  created_at: string;
  room_id: string;
  sender_id: string;
  content: string;
  is_room_message: boolean;
  is_dm: boolean;
  profiles?: UserProfile;
}

interface ChatState {
  messages: RoomMessage[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ChatState = {
  messages: [],
  status: 'idle',
  error: null,
};

// Async thunks
export const sendRoomMessage = createAsyncThunk(
  'chat/sendRoomMessage',
  async (
    {
      roomId,
      senderId,
      content,
    }: {roomId: string; senderId: string; content: string},
    {rejectWithValue},
  ) => {
    try {
      // 1. Insert the message
      const {data: newMessage, error: insertError} = await supabase
        .from('messages')
        .insert([
          {
            room_id: roomId,
            sender_id: senderId,
            content: content,
            is_room_message: true,
            is_dm: false, // Assuming this is for room chat
          },
        ])
        .select() // Select the inserted row
        .single();

      if (insertError) {
        return rejectWithValue(insertError.message);
      }

      if (!newMessage) {
        return rejectWithValue('Failed to retrieve sent message data');
      }

      // 2. Fetch sender's profile
      const {data: profile, error: profileError} = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url') // Select needed fields
        .eq('id', senderId)
        .maybeSingle(); // Use maybeSingle in case profile doesn't exist

      if (profileError) {
        // Proceed without profile if fetch fails
        return newMessage as RoomMessage;
      }

      // 3. Combine message and profile
      const messageWithProfile = {
        ...newMessage,
        profiles: profile
          ? {
              // Use correct field name 'profiles'
              id: profile.id,
              full_name: profile.full_name,
              avatar_url: profile.avatar_url,
            }
          : undefined, // Set to undefined if profile is null
      } as RoomMessage;

      return messageWithProfile;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to send message');
    }
  },
);

export const getRoomMessages = createAsyncThunk(
  'chat/getRoomMessages',
  async (roomId: string, {rejectWithValue}) => {
    try {
      // 1. Fetch messages
      const {data: messages, error: messagesError} = await supabase
        .from('messages')
        .select('*') // Select all message fields
        .eq('room_id', roomId)
        .eq('is_room_message', true)
        .order('created_at', {ascending: true});

      if (messagesError) {
        return rejectWithValue(messagesError.message);
      }

      if (!messages || messages.length === 0) {
        return []; // Return empty array if no messages
      }

      // 2. Get unique sender IDs
      const senderIds = [...new Set(messages.map(msg => msg.sender_id))];

      if (senderIds.length === 0) {
        return messages as RoomMessage[]; // Should not happen if messages exist, but safety check
      }

      // 3. Fetch profiles for sender IDs
      const {data: profiles, error: profilesError} = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url') // Select needed fields
        .in('id', senderIds);

      if (profilesError) {
        // Return messages without profiles if profile fetch fails
        return messages as RoomMessage[];
      }

      // 4. Map profiles to messages
      const messagesWithProfiles = messages.map(message => ({
        ...message,
        profiles: profiles?.find(p => p.id === message.sender_id) || undefined,
      }));

      return (messagesWithProfiles as RoomMessage[]) || [];
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to get messages');
    }
  },
);

// Slice
const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addRoomMessage: (state, action: PayloadAction<RoomMessage>) => {
      if (!state.messages.some(msg => msg.id === action.payload.id)) {
        state.messages.push(action.payload);
      }
    },
    setRoomMessages: (state, action: PayloadAction<RoomMessage[]>) => {
      state.messages = action.payload;
    },
    clearRoomMessages: state => {
      state.messages = [];
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(sendRoomMessage.pending, state => {
        state.status = 'loading';
      })
      .addCase(sendRoomMessage.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (action.payload) {
          if (!state.messages.some(msg => msg.id === action.payload.id)) {
            state.messages.push(action.payload);
          }
        }
      })
      .addCase(sendRoomMessage.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })

      .addCase(getRoomMessages.pending, state => {
        state.status = 'loading';
      })
      .addCase(getRoomMessages.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.messages = action.payload;
      })
      .addCase(getRoomMessages.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });
  },
});

export const {addRoomMessage, setRoomMessages, clearRoomMessages} =
  chatSlice.actions;
export default chatSlice.reducer;
